/**
 * Single source of truth for content/property roles.
 * Each entry: API key, UI label, chip class (colors in ViewAccount.css), sidebar modifier,
 * permission copy, and invite/edit dropdown flags.
 */

const ADMIN_PERMISSIONS = [
  { title: "Invite or remove Account users", description: "Add and remove users from the account" },
  { title: "Assign Content Roles", description: "Change user roles and permissions" },
  { title: "Assign another user as Manager", description: "Promote users to Manager role" },
  { title: "Upload media", description: "Add images or videos to Asset Manager" },
  { title: "Draft posts", description: "Create and edit draft content" },
  { title: "Approve posts", description: "Review and approve content for publishing" },
  { title: "Publish posts", description: "Publish approved content to social channels" },
]

export const CONTENT_ROLES = [
  {
    apiRole: "ROLE_OWNER",
    label: "Owner",
    chipClass: "owner",
    sidebarModifier: "owner",
    permissions: [],
  },
  {
    apiRole: "ROLE_ADMIN",
    label: "Admin",
    chipClass: "admin",
    sidebarModifier: "admin",
    permissions: ADMIN_PERMISSIONS,
    inviteSelectable: true,
    inviteOwnerOnly: true,
    inviteOrder: 0,
  },
  {
    apiRole: "ROLE_ACCOUNT_MANAGER",
    label: "Account Manager",
    chipClass: "account_manager",
    sidebarModifier: "manager",
    permissions: ADMIN_PERMISSIONS.slice(1),
    inviteSelectable: true,
    editSelectable: true,
    inviteOrder: 1,
    editOrder: 1,
  },
  {
    apiRole: "ROLE_PUBLISHER",
    label: "Publisher",
    chipClass: "publisher",
    sidebarModifier: "publisher",
    permissions: [
      { title: "Upload media", description: "Add images or videos to Asset Manager" },
      { title: "Draft posts", description: "Create and edit draft content" },
      { title: "Publish posts", description: "Publish approved content to social channels" },
    ],
    inviteSelectable: true,
    editSelectable: true,
    inviteOrder: 2,
    editOrder: 2,
  },
  {
    apiRole: "ROLE_CREATOR",
    label: "Creator",
    chipClass: "creator",
    sidebarModifier: "creator",
    permissions: [
      { title: "Upload media", description: "Add images or videos to Asset Manager" },
      { title: "Draft posts", description: "Create and edit draft content" },
    ],
    inviteSelectable: true,
    editSelectable: true,
    inviteOrder: 3,
    editOrder: 4,
  },
  {
    apiRole: "ROLE_APPROVER",
    label: "Approver",
    chipClass: "approver",
    sidebarModifier: "approver",
    permissions: [
      { title: "Upload media", description: "Add images or videos to Asset Manager" },
      { title: "Approve posts", description: "Review and approve content for publishing" },
      { title: "Publish posts", description: "Publish approved content to social channels" },
    ],
    inviteSelectable: true,
    editSelectable: true,
    inviteOrder: 4,
    editOrder: 5,
  },
  {
    apiRole: "ROLE_MODERATOR",
    label: "Moderator",
    chipClass: "moderator",
    sidebarModifier: "moderator",
    permissions: [
      {
        title: "Monitor and respond to guest messages",
        description: "View incoming conversations and send replies across connected social channels.",
      },
      {
        title: "Escalate issues to property teams",
        description: "Create property alerts when assistance from the hotel team is required.",
      },
      {
        title: "Access and maintain conversation context",
        description: "View and update guest interaction history, property notes, and AI-identified risk signals.",
      },
    ],
    inviteSelectable: true,
    editSelectable: true,
    inviteOrder: 5,
    editOrder: 3,
  },
  {
    apiRole: "ROLE_COLLABORATIVE_PUBLISHER",
    label: "Collaborative Publisher",
    chipClass: "collaborative_publisher",
    sidebarModifier: "collab-pub",
    permissions: [
      { title: "Upload media", description: "Add images or videos to Asset Manager" },
      { title: "Draft posts", description: "Create and edit draft content" },
      { title: "Approve posts", description: "Review and approve content for publishing" },
      { title: "Publish posts", description: "Publish approved content to social channels" },
    ],
    inviteSelectable: true,
    editSelectable: true,
    inviteOrder: 6,
    editOrder: 6,
  },
]

const CONTENT_ROLE_BY_API = Object.fromEntries(CONTENT_ROLES.map((role) => [role.apiRole, role]))
const CONTENT_ROLE_BY_LABEL = Object.fromEntries(CONTENT_ROLES.map((role) => [role.label, role]))
const CONTENT_ROLE_BY_LABEL_LOWER = Object.fromEntries(
  CONTENT_ROLES.map((role) => [role.label.toLowerCase(), role]),
)

/** Permission copy keyed by UI label (legacy shape for getRolePermissionsForRole). */
export const ROLE_PERMISSIONS_BY_ROLE = Object.fromEntries(
  CONTENT_ROLES.map((role) => [role.label, role.permissions]),
)

export function normalizeRoleKey(role) {
  if (role == null || String(role).trim() === "") return ""
  const raw = String(role).trim()
  if (raw.startsWith("ROLE_")) return raw.replace(/\s+$/, "")
  const upper = raw.toUpperCase().replace(/[\s-]+/g, "_")
  if (upper === "MANAGER" || upper === "ACCOUNT_MANAGER") return "ROLE_ACCOUNT_MANAGER"
  if (upper === "COLLABORATIVE_PUBLISHER") return "ROLE_COLLABORATIVE_PUBLISHER"
  return `ROLE_${upper}`
}

export function findContentRole(role) {
  if (role == null || String(role).trim() === "") return null
  const apiKey = normalizeRoleKey(role)
  if (CONTENT_ROLE_BY_API[apiKey]) return CONTENT_ROLE_BY_API[apiKey]
  const raw = String(role).trim()
  if (CONTENT_ROLE_BY_LABEL[raw]) return CONTENT_ROLE_BY_LABEL[raw]
  const lower = raw.toLowerCase()
  if (CONTENT_ROLE_BY_LABEL_LOWER[lower]) return CONTENT_ROLE_BY_LABEL_LOWER[lower]
  return null
}

export function getRolePermissionsForRole(roleName) {
  return findContentRole(roleName)?.permissions ?? []
}

export function uiLabelToApiRole(label) {
  const role = findContentRole(label)
  if (role) return role.apiRole
  const key = normalizeRoleKey(label)
  return key || label
}

export function apiRoleOrAliasToUiLabel(role) {
  const match = findContentRole(role)
  if (match) return match.label
  const raw = String(role ?? "").trim()
  if (!raw) return ""
  return raw
    .replace(/^ROLE_/i, "")
    .toLowerCase()
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
}

export function getRoleChipClass(role) {
  return findContentRole(role)?.chipClass ?? "default"
}

export function getRoleSidebarModifier(role) {
  return findContentRole(role)?.sidebarModifier ?? "default"
}

export function getRoleUILabel(role) {
  return findContentRole(role)?.label ?? ""
}

function toDropdownOption(role) {
  return { id: role.label, label: role.label, value: role.label }
}

export const ORG_EDIT_USER_ROLE_OPTIONS = CONTENT_ROLES
  .filter((role) => role.editSelectable)
  .sort((a, b) => a.editOrder - b.editOrder)
  .map(toDropdownOption)

export function getOrgInviteUserRoleOptions(includeAdmin = false) {
  return CONTENT_ROLES
    .filter((role) => {
      if (!role.inviteSelectable) return false
      if (role.inviteOwnerOnly) return includeAdmin
      return true
    })
    .sort((a, b) => a.inviteOrder - b.inviteOrder)
    .map(toDropdownOption)
}
